import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Product, Category } from '../../types/models';
import { useData } from '../../contexts/DataContext';
interface ProductFormProps {
  isOpen: boolean;
  onClose: () => void;
  product?: Product;
}
export function ProductForm({
  isOpen,
  onClose,
  product
}: ProductFormProps) {
  const {
    addProduct,
    updateProduct,
    categories
  } = useData();
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    category: '',
    image: '',
    stock: '',
    description: '',
    isNew: false,
    isSpecialEvent: false,
    specialEventStartDate: '',
    specialEventEndDate: ''
  });
  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name,
        price: product.price.toString(),
        category: product.category,
        image: product.image,
        stock: product.stock.toString(),
        description: product.description || '',
        isNew: product.isNew || false,
        isSpecialEvent: product.isSpecialEvent || false,
        specialEventStartDate: product.specialEventStartDate || '',
        specialEventEndDate: product.specialEventEndDate || ''
      });
    } else {
      setFormData({
        name: '',
        price: '',
        category: categories[0]?.title || '',
        image: '',
        stock: '',
        description: '',
        isNew: false,
        isSpecialEvent: false,
        specialEventStartDate: '',
        specialEventEndDate: ''
      });
    }
  }, [product, categories, isOpen]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const productData = {
      name: formData.name,
      price: parseFloat(formData.price),
      category: formData.category,
      image: formData.image,
      stock: parseInt(formData.stock),
      description: formData.description,
      isNew: formData.isNew,
      isSpecialEvent: formData.isSpecialEvent,
      specialEventStartDate: formData.specialEventStartDate || undefined,
      specialEventEndDate: formData.specialEventEndDate || undefined
    };
    if (product) {
      updateProduct(product.id, productData);
    } else {
      addProduct(productData);
    }
    onClose();
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-276">
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-id="element-277">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-xl" data-id="element-278">
          <div className="p-6 border-b border-gray-100 flex justify-between items-center" data-id="element-279">
            <h2 className="text-xl font-bold font-serif text-gray-900" data-id="element-280">
              {product ? 'Edit Product' : 'Add New Product'}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500 transition-colors" data-id="element-281">
              <X size={24} data-id="element-282" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4" data-id="element-283">
            <Input label="Product Name" value={formData.name} onChange={e => setFormData({
            ...formData,
            name: e.target.value
          })} placeholder="e.g. Luxury Rose Box" required data-id="element-284" />

            <div className="grid grid-cols-2 gap-4" data-id="element-285">
              <Input label="Price (ZAR)" type="number" value={formData.price} onChange={e => setFormData({
              ...formData,
              price: e.target.value
            })} placeholder="0.00" required data-id="element-286" />
              <Input label="Stock" type="number" value={formData.stock} onChange={e => setFormData({
              ...formData,
              stock: e.target.value
            })} placeholder="0" required data-id="element-287" />
            </div>

            <div className="space-y-1" data-id="element-288">
              <label className="block text-sm font-medium text-gray-700" data-id="element-289">
                Category
              </label>
              <select value={formData.category} onChange={e => setFormData({
              ...formData,
              category: e.target.value
            })} className="w-full px-4 py-2 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all" required data-id="element-290">
                {categories.map(cat => <option key={cat.id} value={cat.title} data-id="element-291">
                    {cat.title}
                  </option>)}
              </select>
            </div>

            <Input label="Image URL" value={formData.image} onChange={e => setFormData({
            ...formData,
            image: e.target.value
          })} placeholder="https://..." required data-id="element-292" />

            <div className="space-y-1" data-id="element-293">
              <label className="block text-sm font-medium text-gray-700" data-id="element-294">
                Description
              </label>
              <textarea value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} className="w-full px-4 py-2 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all h-24 resize-none" placeholder="Product description..." data-id="element-295" />
            </div>

            <div className="flex items-center space-x-2" data-id="element-296">
              <input type="checkbox" id="isNew" checked={formData.isNew} onChange={e => setFormData({
              ...formData,
              isNew: e.target.checked
            })} className="rounded border-gray-300 text-rose-500 focus:ring-rose-500" data-id="element-297" />
              <label htmlFor="isNew" className="text-sm text-gray-700" data-id="element-298">
                Mark as New Arrival
              </label>
            </div>

            {/* Special Event Section */}
            <div className="pt-4 border-t border-gray-200" data-id="element-299">
              <div className="flex items-center space-x-2 mb-4" data-id="element-300">
                <input type="checkbox" id="isSpecialEvent" checked={formData.isSpecialEvent} onChange={e => setFormData({
                ...formData,
                isSpecialEvent: e.target.checked
              })} className="rounded border-gray-300 text-rose-500 focus:ring-rose-500" data-id="element-301" />
                <label htmlFor="isSpecialEvent" className="text-sm font-medium text-gray-700" data-id="element-302">
                  Special Event Product (Admin Only)
                </label>
              </div>

              {formData.isSpecialEvent && <div className="grid grid-cols-2 gap-4 pl-6" data-id="element-303">
                  <div className="space-y-1" data-id="element-304">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-305">
                      Start Date
                    </label>
                    <input type="date" value={formData.specialEventStartDate} onChange={e => setFormData({
                  ...formData,
                  specialEventStartDate: e.target.value
                })} className="w-full px-4 py-2 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all" required={formData.isSpecialEvent} data-id="element-306" />
                  </div>
                  <div className="space-y-1" data-id="element-307">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-308">
                      End Date
                    </label>
                    <input type="date" value={formData.specialEventEndDate} onChange={e => setFormData({
                  ...formData,
                  specialEventEndDate: e.target.value
                })} className="w-full px-4 py-2 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all" required={formData.isSpecialEvent} data-id="element-309" />
                  </div>
                </div>}
            </div>

            <div className="flex justify-end space-x-3 pt-4" data-id="element-310">
              <Button type="button" variant="outline" onClick={onClose} data-id="element-311">
                Cancel
              </Button>
              <Button type="submit" data-id="element-312">
                {product ? 'Save Changes' : 'Create Product'}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>;
}